import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';

interface SettingsListDeleteButtonProps {
  id: string;
  deletingId: string | null;
  onDelete: (id: string) => void | Promise<void>;
  /** Optional row summary shown in the dialog (e.g. name, code). */
  itemLabel?: string;
  /**
   * When set (e.g. on author rows), shows how many books reference this row.
   * If greater than zero, delete is blocked in the UI.
   */
  linkedBooksCount?: number;
}

export default function SettingsListDeleteButton({
  id,
  deletingId,
  onDelete,
  itemLabel,
  linkedBooksCount,
}: SettingsListDeleteButtonProps) {
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    if (deletingId === id) return;
    setOpen(false);
  };

  const blockDeleteForBooks = linkedBooksCount != null && linkedBooksCount > 0;

  const handleConfirm = () => {
    if (blockDeleteForBooks) return;
    void Promise.resolve(onDelete(id)).finally(() => {
      setOpen(false);
    });
  };

  return (
    <>
      <Tooltip title="Устгах">
        <span>
          <IconButton
            color="error"
            size="small"
            aria-label="Устгах"
            disabled={deletingId === id}
            onClick={() => setOpen(true)}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="settings-delete-dialog-title"
        aria-describedby="settings-delete-dialog-description"
      >
        <DialogTitle id="settings-delete-dialog-title">Устгах уу?</DialogTitle>
        <DialogContent>
          <DialogContentText id="settings-delete-dialog-description" component="div">
            Энэ номыг устгахдаа итгэлтэй байна уу? Дахин сэргээх боломжгүй.
            {itemLabel != null && String(itemLabel).trim() !== '' && (
              <Box
                component="span"
                sx={{ display: 'block', mt: 1, fontWeight: 600, whiteSpace: 'pre-line' }}
              >
                {itemLabel.trim()}
              </Box>
            )}
            {linkedBooksCount != null && (
              <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
                Холбогдсон номын тоо: {linkedBooksCount}
              </Box>
            )}
            {blockDeleteForBooks && (
              <Box component="span" sx={{ display: 'block', mt: 1.5, color: 'error.main' }}>
                Энэ зохиолчийг устгах боломжгүй. Эхлээд холбогдсон номуудыг устгах эсвэл өөр зохиолчид холбоно уу.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={deletingId === id}>
            Болих
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === id || blockDeleteForBooks}
          >
            Устгах
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
